import { useState } from "react";
import {
  AlertTriangle,
  ClipboardCheck,
  Droplets,
  Sprout,
  TrendingUp,
  Wrench,
} from "lucide-react";
import WaterSafetySchoolsModal from "@/components/WaterSafetySchoolsModal";
import HeroStatusChip from "@/components/HeroStatusChip";
import {
  buildWaterActionQueue,
  buildWaterClosurePlan,
  deriveTestingTarget,
  fmt,
  pct,
} from "@/utils/waterSafetyDerived";
import WaterSafetyGuidanceChip from "@/components/WaterSafetyGuidanceChip";
import { toTitleCase } from "../utils/toTitleCase";

const buildWsiGaugeStyle = (value) => {
  const color =
    value >= 90 ? "var(--inf-success)" : value >= 75 ? "var(--inf-warning)" : "var(--inf-danger)";
  const track =
    value >= 90
      ? "rgba(16,185,129,.14)"
      : value >= 75
        ? "rgba(245,158,11,.14)"
        : "rgba(239,68,68,.14)";
  return {
    background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(${color} 0 ${Math.min(value, 100)}%, ${track} ${Math.min(value, 100)}% 100%)`,
  };
};

const buildTapDonutStyle = (value) => ({
  background: `conic-gradient(var(--inf-success) 0 ${Math.min(value, 100)}%, var(--inf-danger) ${Math.min(value, 100)}% 100%)`,
});

const buildPurificationDonutStyle = (funcPct, nonFuncPct) => {
  const endFunc = Math.min(funcPct, 100);
  const endNonFunc = Math.min(endFunc + nonFuncPct, 100);
  return {
    background: `conic-gradient(var(--inf-success) 0 ${endFunc}%, var(--inf-warning) ${endFunc}% ${endNonFunc}%, var(--inf-danger) ${endNonFunc}% 100%)`,
  };
};

const getRiskLabel = (wsi) => {
  if (wsi >= 90) return "Low Risk";
  if (wsi >= 75) return "Moderate Risk";
  return "High Risk";
};

const getBlockStatus = (wsi) => {
  if (wsi >= 90) return { label: "Stable", tone: "green" };
  if (wsi >= 75) return { label: "Watch", tone: "" };
  return { label: "Priority", tone: "red" };
};

const getBlockAction = (block) => {
  if (block.purification_pct < 60) return "Purifier installation sprint";
  if (block.water_testing_pct < 80 && block.purification_pct < 80) return "Testing + purification closure";
  if (block.purification_pct < 80) return "Improve purification coverage";
  if (block.purification_pct < 85) return "Close purifier repair backlog";
  if (block.water_testing_pct < 95) return "Maintain testing cadence";
  return "Document best practices";
};

const LegendItem = ({ tone, title, description, value }) => (
  <div className="inf-legend-item">
    <span className={`inf-legend-dot ${tone}`} />
    <div className="inf-legend-copy">
      <b>{title}</b>
      <span>{description}</span>
    </div>
    <strong>{fmt(value)}</strong>
  </div>
);

const ReadinessCard = ({ icon: Icon, iconTone, pillLabel, pillTone, title, value, unit, progressPct, progressColor, metaLeft, metaRight }) => (
  <article className="inf-readiness-card">
    <div className="inf-readiness-head">
      <div className={`inf-icon-box ${iconTone}`}>
        <Icon size={22} />
      </div>
      <span className={`inf-pill ${pillTone}`}>{pillLabel}</span>
    </div>
    <h3>{toTitleCase(title)}</h3>
    <div className="inf-readiness-score">
      <strong>{value}</strong>
      <span>{unit}</span>
    </div>
    <div className="inf-progress-line">
      <i style={{ width: `${Math.min(progressPct, 100)}%`, background: progressColor }} />
    </div>
    <div className="inf-readiness-meta">
      <span>{metaLeft}</span>
      <span>{metaRight}</span>
    </div>
  </article>
);

const WaterSafetyWaterTab = ({
  overview,
  blockData,
  waterDistribution,
  bottomBlocks,
  guidance,
  guidanceProvider = "local",
  guidanceLoading = false,
  scopeLabel = "Pune District",
}) => {
  const [waterMetricModal, setWaterMetricModal] = useState(null);

  if (!overview) {
    return (
      <div className="inf-empty-card">
        <Droplets className="mx-auto mb-3 opacity-40" size={40} />
        <p>No water safety data available for the selected scope.</p>
      </div>
    );
  }

  const wsi = overview.water_safety_index ?? 0;
  const tapPct = overview.tap_water_coverage ?? 0;
  const purifPct = overview.purification_coverage ?? 0;
  const testingPct = overview.water_testing_coverage ?? 0;
  const rwhPct = overview.rwh_coverage ?? 0;
  const purifNonFuncPct = overview.purification_non_func_pct ?? 0;
  const purifNo = overview.purification_no ?? 0;
  const purifNonFunc = overview.purification_non_func ?? 0;
  const tapYes = overview.tap_water_yes ?? 0;
  const tapNo = overview.tap_water_no ?? 0;
  const purifYes = overview.purification_yes ?? 0;

  const tapDist = waterDistribution?.tap_water ?? [
    { name: "Available", value: tapYes },
    { name: "Not Available", value: tapNo },
  ];
  const purifDist = waterDistribution?.purification ?? [
    { name: "Functional", value: purifYes },
    { name: "Non-Functional", value: purifNonFunc },
    { name: "Not Available", value: purifNo },
  ];

  const rankedBlocks = [...(blockData ?? [])].sort(
    (a, b) => (b.water_safety_index ?? 0) - (a.water_safety_index ?? 0)
  );

  const tableBlocks = rankedBlocks.slice(0, 12);
  const testingTarget = deriveTestingTarget(testingPct, overview.total_schools, overview.water_testing_yes);
  const localActionQueue = buildWaterActionQueue(overview, blockData, bottomBlocks);
  const actionQueue = (guidance?.action_queue?.length ? guidance.action_queue : localActionQueue).map((action, index) => ({
    title: action.title,
    description: action.description,
    priority: action.priority ?? {
      label: action.priority_label ?? "Medium",
      tone: action.priority_label === "Critical" ? "high" : action.priority_label === "High" ? "medium" : "low",
    },
    focus: action.focus ?? localActionQueue[index]?.focus,
  }));
  const heroSummary =
    guidance?.hero_summary ??
    `WSI is ${wsi.toFixed(1)} with tap water at ${pct(tapPct)} and purification at ${pct(purifPct)}. ${fmt(purifNo)} schools lack purification, ${fmt(purifNonFunc)} units are non-functional, and RWH covers ${pct(rwhPct)} (${fmt(overview.rwh_yes)} schools).`;
  const primaryAction =
    guidance?.primary_action ??
    `Convert the ${fmt(purifNo)} schools without purification into a block-wise remediation queue and review progress every Friday.`;
  const closurePlanText =
    guidance?.closure_plan ?? buildWaterClosurePlan(overview, blockData, bottomBlocks);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="inf-water-tab" data-testid="tab-water-content" id="inf-water-overview">
      <section className="inf-hero-card inf-hero-card-water">
        <div className="inf-hero-layout">
          <div>
            <div className="inf-health-main">
              <div
                className="inf-wsi-gauge"
                style={buildWsiGaugeStyle(wsi)}
                aria-label={`Water Safety Index ${wsi}`}
              >
                <div>
                  <strong>{wsi.toFixed(1)}</strong>
                  <span>WSI Score</span>
                </div>
              </div>
              <div className="inf-hero-copy">
                <h2>Water Safety Command Centre</h2>
                <p>{heroSummary}</p>
                <div className="inf-status-row">
                  <HeroStatusChip
                    chipClass="inf-status-chip"
                    onClick={wsi < 85 ? () => scrollTo("inf-water-actions") : undefined}
                    ariaLabel={wsi < 85 ? `${getRiskLabel(wsi)} — view water safety actions` : undefined}
                  >
                    {getRiskLabel(wsi)}
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="inf-status-chip"
                    variant="blue"
                    onClick={tapNo > 0 ? () => setWaterMetricModal("tap_water") : undefined}
                    ariaLabel={
                      tapNo > 0 ? `${fmt(tapYes)} tap-water schools — view schools without tap water` : undefined
                    }
                  >
                    {fmt(tapYes)} tap-water schools
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="inf-status-chip"
                    variant="green"
                    onClick={
                      testingPct < testingTarget ? () => scrollTo("inf-water-diagnostics") : undefined
                    }
                    ariaLabel={
                      testingPct < testingTarget
                        ? `${pct(testingPct)} tested — view water testing diagnostics`
                        : undefined
                    }
                  >
                    {pct(testingPct)} tested
                  </HeroStatusChip>
                  <WaterSafetyGuidanceChip provider={guidanceProvider} loading={guidanceLoading} />
                </div>
              </div>
            </div>
            <div className="inf-summary-note">
              <div className="inf-icon-box cyan">
                <Droplets size={22} />
              </div>
              <div>
                <b>Primary action for officers</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="inf-metric-grid" aria-label="Water safety KPIs">
            <article className="inf-metric-card">
              <small>Total Schools</small>
              <strong>{fmt(overview.total_schools)}</strong>
              <span>Pune District scope</span>
            </article>
            <article
              className="inf-metric-card clickable"
              role="button"
              tabIndex={0}
              aria-label="Tap Water: view schools without tap water"
              onClick={() => setWaterMetricModal("tap_water")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setWaterMetricModal("tap_water");
                }
              }}
            >
              <small>Tap Water</small>
              <strong>{pct(tapPct, 0)}</strong>
              <span>{fmt(tapYes)} schools available</span>
            </article>
            <article
              className="inf-metric-card warn clickable"
              role="button"
              tabIndex={0}
              aria-label="Purification: view schools without purification"
              onClick={() => setWaterMetricModal("purification")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setWaterMetricModal("purification");
                }
              }}
            >
              <small>Purification</small>
              <strong>{pct(purifPct)}</strong>
              <span>Needs catch-up</span>
            </article>
            <article
              className="inf-metric-card warn clickable"
              role="button"
              tabIndex={0}
              aria-label="Rainwater Harvesting: view schools without RWH"
              onClick={() => setWaterMetricModal("rainwater_harvesting")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setWaterMetricModal("rainwater_harvesting");
                }
              }}
            >
              <small>Rainwater Harvesting</small>
              <strong>{pct(rwhPct)}</strong>
              <span>High opportunity area</span>
            </article>
          </div>
        </div>
      </section>

      <div className="inf-section-title" id="inf-water-diagnostics">
        <div>
          <h2>Availability and Purification Diagnostics</h2>
          <p>
            Large raw charts are converted into decision-ready visuals with counts, percentage split and
            immediate interpretation.
          </p>
        </div>
        <button type="button" className="inf-section-action" onClick={() => scrollTo("inf-water-actions")}>
          View action queue →
        </button>
      </div>

      <section className="inf-diagnostic-grid">
        <article className="inf-panel">
          <div className="inf-panel-head">
            <div>
              <h3>Tap Water Availability</h3>
              <p>
                Most schools have tap water, but {fmt(tapNo)} schools still require basic access closure.
              </p>
            </div>
            <span className="inf-pill inf-pill-green">{pct(tapPct, 0)} available</span>
          </div>
          <div className="inf-donut-layout">
            <div className="inf-donut tap" style={buildTapDonutStyle(tapPct)}>
              <div className="inf-donut-center">
                <div>
                  <strong>{pct(tapPct, 0)}</strong>
                  <span>Tap Water</span>
                </div>
              </div>
            </div>
            <div className="inf-legend-list">
              {tapDist.map((item) => (
                <LegendItem
                  key={item.name}
                  tone={item.name.toLowerCase().includes("not") ? "red" : ""}
                  title={item.name}
                  description={item.name.toLowerCase().includes("not") ? "Requires basic access" : "Schools with tap water"}
                  value={item.value}
                />
              ))}
            </div>
          </div>
        </article>

        <article className="inf-panel">
          <div className="inf-panel-head">
            <div>
              <h3>Purification Status</h3>
              <p>Purification is the largest controllable gap in the water-safety stack.</p>
            </div>
            <span className="inf-pill inf-pill-amber">{pct(purifPct)} functional</span>
          </div>
          <div className="inf-donut-layout">
            <div
              className="inf-donut purification"
              style={buildPurificationDonutStyle(purifPct, purifNonFuncPct)}
            >
              <div className="inf-donut-center">
                <div>
                  <strong>{pct(purifPct)}</strong>
                  <span>Functional</span>
                </div>
              </div>
            </div>
            <div className="inf-legend-list">
              {purifDist.map((item) => {
                const tone = item.name.toLowerCase().includes("non")
                  ? "orange"
                  : item.name.toLowerCase().includes("not")
                    ? "red"
                    : "";
                const desc =
                  item.name === "Functional"
                    ? "Ready for safe use"
                    : item.name === "Non-Functional"
                      ? "Repair required"
                      : "New setup required";
                return (
                  <LegendItem key={item.name} tone={tone} title={item.name} description={desc} value={item.value} />
                );
              })}
            </div>
          </div>
        </article>
      </section>

      <div className="inf-section-title" id="inf-water-readiness">
        <div>
          <h2>Testing, Safety and Harvesting Readiness</h2>
          <p>Focus on the indicators that materially affect safe drinking water reliability and future resilience.</p>
        </div>
        <button type="button" className="inf-section-action" onClick={() => scrollTo("inf-water-ranking")}>
          Go to block ranking →
        </button>
      </div>

      <section className="inf-readiness-grid">
        <ReadinessCard
          icon={ClipboardCheck}
          iconTone=""
          pillLabel="Needs push"
          pillTone="inf-pill-amber"
          title="Water testing coverage"
          value={testingPct.toFixed(1)}
          unit="%"
          progressPct={testingPct}
          progressColor="var(--inf-warning)"
          metaLeft="Testing completed"
          metaRight={`Target ${pct(testingTarget)}`}
        />
        <ReadinessCard
          icon={TrendingUp}
          iconTone="orange"
          pillLabel="Low adoption"
          pillTone="inf-pill-red"
          title="Rainwater harvesting"
          value={rwhPct.toFixed(1)}
          unit="%"
          progressPct={rwhPct}
          progressColor="var(--inf-secondary)"
          metaLeft="Resilience gap"
          metaRight="Block-level plan"
        />
        <ReadinessCard
          icon={TrendingUp}
          iconTone="cyan"
          pillLabel="Composite"
          pillTone="inf-pill-amber"
          title="Water Safety Index"
          value={wsi.toFixed(1)}
          unit="score"
          progressPct={wsi}
          progressColor="var(--inf-cyan)"
          metaLeft="Tap + Purification + Testing"
          metaRight={wsi >= 90 ? "Strong" : wsi >= 75 ? "Moderate" : "Needs focus"}
        />
      </section>

      <div className="inf-section-title" id="inf-water-ranking">
        <div>
          <h2>Block-Wise Water Safety Ranking</h2>
          <p>The bar ranking highlights which blocks are stable and which need a targeted safety sprint.</p>
        </div>
        <button type="button" className="inf-section-action" onClick={() => scrollTo("inf-water-overview")}>
          Back to top ↑
        </button>
      </div>

      <section className="inf-ranking-grid">
        <article className="inf-panel">
          <div className="inf-panel-head">
            <div>
              <h3>Water Safety Index by Block</h3>
              <p>Composite score = tap water, purification and testing readiness.</p>
            </div>
            <span className="inf-pill">Top to bottom</span>
          </div>
          <div className="inf-bar-list" aria-label="Block-wise water safety bar chart">
            {rankedBlocks.map((block) => {
              const score = block.water_safety_index ?? 0;
              const barTone = score >= 90 ? "" : "amber";
              return (
                <div className="inf-hbar-row" key={block.block_name}>
                  <div className="inf-hbar-label">{block.block_name}</div>
                  <div className="inf-hbar-track">
                    <div className={`inf-hbar ${barTone}`} style={{ width: `${Math.min(score, 100)}%` }} />
                  </div>
                  <div className="inf-hbar-value">{score.toFixed(1)}</div>
                </div>
              );
            })}
          </div>
        </article>

        <article className="inf-panel" id="inf-water-actions">
          <div className="inf-panel-head">
            <div>
              <h3>Officer Action Queue</h3>
              <p>Prioritized actions designed for weekly district review.</p>
            </div>
            <span className="inf-pill inf-pill-amber">30-day sprint</span>
          </div>
          <div className="inf-action-card">
            {actionQueue.map((action, index) => {
              const icons = [AlertTriangle, Wrench, ClipboardCheck, Sprout];
              const tones = ["red", "orange", "cyan", "green"];
              const Icon = icons[index] ?? Sprout;
              return (
                <div className="inf-action-item" key={action.title}>
                  <div className={`inf-icon-box ${tones[index] ?? "green"}`}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{action.title}</h4>
                    <p>{action.description}</p>
                  </div>
                  <span className={`inf-priority ${action.priority.tone}`}>{action.priority.label}</span>
                </div>
              );
            })}
          </div>
          <div className="inf-plan-card">
            <b>30-day closure plan</b>
            <span>{closurePlanText}</span>
          </div>
        </article>
      </section>

      <section className="inf-panel inf-table-card">
        <div className="inf-table-head">
          <div>
            <h3>Block Action Table</h3>
            <p>Use this table to move from observation to ownership, timeline and review status.</p>
          </div>
          <span className="inf-pill">Operational tracker</span>
        </div>
        <div className="inf-table-wrap">
          <table className="inf-data-table" aria-label="Block-wise water safety action table">
            <thead>
              <tr>
                <th>Block</th>
                <th>WSI</th>
                <th>Tap Water</th>
                <th>Purification</th>
                <th>Testing</th>
                <th>Rainwater Harvesting</th>
                <th>Priority Action</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {tableBlocks.map((block) => {
                const status = getBlockStatus(block.water_safety_index ?? 0);
                return (
                  <tr key={block.block_name}>
                    <td>{block.block_name}</td>
                    <td className="score">{block.water_safety_index?.toFixed(1) ?? "—"}</td>
                    <td>{pct(block.tap_water_pct, 0)}</td>
                    <td>{pct(block.purification_pct, 0)}</td>
                    <td>{pct(block.water_testing_pct, 0)}</td>
                    <td>{pct(block.rwh_pct, 0)}</td>
                    <td>{getBlockAction(block)}</td>
                    <td>
                      <span className={`inf-tag ${status.tone}`}>{status.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <WaterSafetySchoolsModal
        open={Boolean(waterMetricModal)}
        metric={waterMetricModal}
        onClose={() => setWaterMetricModal(null)}
        scopeLabel={scopeLabel}
        districtPct={
          waterMetricModal === "tap_water"
            ? tapPct
            : waterMetricModal === "purification"
              ? purifPct
              : waterMetricModal === "rainwater_harvesting"
                ? rwhPct
                : null
        }
      />
    </div>
  );
};

export default WaterSafetyWaterTab;
